import { rmsApi } from '../../../state/axios';

const BASE = '/api/v2/ask-vantage';
const BASE_V1 = '/api/v1/ask-vantage';

// ── Token usage / purchasing (share V1 endpoints) ──────────────────────────

export interface TokenUsageStats {
  total_available_tokens: number;
  remaining_purchased_tokens: number;
  daily_free_quota: number;
  daily_free_usage: number;
  daily_free_remaining: number;
  daily_free_usage_percent: number;
  daily_total_consumed: number;
  purchased_tokens_used_today: number;
  quota_status: 'healthy' | 'warning' | 'daily_limit_reached' | 'exhausted';
  is_quota_exceeded: boolean;
  is_daily_quota_exhausted: boolean;
  show_warning: boolean;
  daily_quota_resets_at: string;
  plan_type: string;
}

export interface TokenUsageResponse {
  display_text: string;
  current_usage: TokenUsageStats;
}

export interface TokenPlan {
  id: number;
  name: string;
  token_count: number;
  price: number | string;
  currency: string;
  description?: string;
}

export interface TokenPlansResponse {
  plans: TokenPlan[];
}

export interface TokenPurchaseResponse {
  success: boolean;
  payment_url?: string;
  error?: string;
}

export const getTokenUsage = async (): Promise<TokenUsageResponse> => {
  const res = await rmsApi.get<TokenUsageResponse>(`${BASE_V1}/token-usage`);
  return res.data;
};

export const getTokenPlans = async (): Promise<TokenPlansResponse> => {
  const res = await rmsApi.get<TokenPlansResponse>(`${BASE_V1}/token-plans`);
  return res.data;
};

export const initiateTokenPurchase = async (planId: number): Promise<TokenPurchaseResponse> => {
  const res = await rmsApi.post<TokenPurchaseResponse>(`${BASE_V1}/token-purchase`, {
    plan_id: planId,
  });
  return res.data;
};

export interface Conversation {
  id: number;
  title: string | null;
  created_at: string;
  updated_at?: string;
}

/** Submitted clarification data merged into `MessageUiState.clarification`. */
export interface ClarificationPayload {
  field_values: Record<string, string>;
  composed_preview: string;
}

export interface ClarificationUiState extends Partial<ClarificationPayload> {
  submitted?: boolean;
  submitted_at?: string;
}

export interface MessageUiState {
  clarification?: ClarificationUiState;
  [key: string]: unknown;
}

export enum MessageVote {
  LIKE = 'like',
  DISLIKE = 'dislike',
  NEUTRAL = 'neutral',
}

export interface Message {
  id: string | number;
  message_type: 'user' | 'assistant';
  content: string;
  html_content: string | null;
  status: string;
  response_version: string;
  ui_state?: MessageUiState;
  stream_stats?: Record<string, unknown> | null;
  created_at: string;
  vote?: MessageVote | string | null;
}

export const createConversation = async (): Promise<Conversation> => {
  const res = await rmsApi.post(`${BASE}/conversations`);
  return res.data;
};

export const listConversations = async (): Promise<Conversation[]> => {
  const res = await rmsApi.get(`${BASE}/conversations`);
  return res.data.conversations;
};

export const deleteConversation = async (conversationId: number): Promise<void> => {
  await rmsApi.delete(`${BASE}/conversations/${conversationId}`);
};

export const fetchMessages = async (conversationId: number): Promise<Message[]> => {
  const res = await rmsApi.get(`${BASE}/conversations/${conversationId}/messages`);
  return res.data.messages;
};

export interface SendMessageResponse {
  message_id: number;
  conversation_id: number;
  pusher_channel: string;
  pusher_event: string;
  streaming_enabled?: boolean;
}

export const sendMessage = async (
  conversationId: number,
  content: string
): Promise<SendMessageResponse> => {
  const res = await rmsApi.post(`${BASE}/conversations/${conversationId}/messages`, {
    message: { content }
  });
  return res.data;
};

export const fetchMessage = async (
  conversationId: number,
  messageId: string | number
): Promise<Message> => {
  const res = await rmsApi.get(`${BASE}/conversations/${conversationId}/messages/${messageId}`);
  return res.data;
};

/** Deep-merge `ui_state` on an assistant message (e.g. clarification form submitted). */
export const patchMessageUiState = async (
  conversationId: number,
  messageId: string | number,
  uiStateFragment: MessageUiState
): Promise<Message> => {
  const res = await rmsApi.patch(`${BASE}/conversations/${conversationId}/messages/${messageId}`, {
    message: { ui_state: uiStateFragment }
  });
  return res.data;
};

export const executeAction = async (
  conversationId: number,
  capability: string,
  params: Record<string, unknown>
): Promise<{ success: boolean; result?: unknown }> => {
  const res = await rmsApi.post(`${BASE}/conversations/${conversationId}/execute_action`, {
    capability,
    params
  });
  return res.data;
};

export const voteMessage = async (
  messageId: string | number,
  vote: MessageVote
): Promise<void> => {
  if (vote === MessageVote.NEUTRAL) {
    await rmsApi.delete(`${BASE_V1}/messages/${messageId}/vote`);
  } else {
    await rmsApi.post(`${BASE_V1}/messages/${messageId}/${vote}`);
  }
};

export interface SampleQuestion {
  id: number;
  question: string;
  domain: string;
}

export interface SampleQuestionsResponse {
  questions: SampleQuestion[];
}

export const getSampleQuestions = async (sample = 6): Promise<SampleQuestion[]> => {
  const res = await rmsApi.get<SampleQuestionsResponse>(
    `${BASE}/sample-questions`,
    { params: { sample } }
  );
  return res.data.questions || [];
};

// ── Business Parameters & KPI Targets ──────────────────────────────────────

export interface BusinessParameter {
  id: number;
  location_id: number | null;
  category: string;
  key: string;
  value: number | string | boolean | Record<string, unknown> | null;
  effective_from: string;
  demo_template: boolean;
  created_at: string;
}

export interface BusinessConfigLocation {
  id: number;
  name: string;
}

export interface BusinessParameterSave {
  category: string;
  key: string;
  value: number | string | boolean | Record<string, unknown>;
  location_id?: number | null;
  effective_from?: string;
}

export interface KpiTarget {
  id: number;
  location_id: number | null;
  category: string;
  metric_key: string;
  period_type: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  value: number;
  effective_from: string;
  demo_template: boolean;
  created_at: string;
}

export interface KpiTargetSave {
  category: string;
  metric_key: string;
  period_type: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  value: number;
  location_id?: number | null;
  effective_from?: string;
}

export interface BusinessParameterCatalogEntry {
  label: string;
  description: string;
  unit: string;
  value_type: 'numeric' | 'enum' | 'map';
  map_keys?: string[];
  enum_values?: string[];
}

export interface BusinessParameterCatalog {
  categories: Record<string, { label: string; keys: Record<string, BusinessParameterCatalogEntry> }>;
}

export interface KpiTargetCatalogEntry {
  label: string;
  description: string;
  unit: string;
  period_types: Array<'daily' | 'weekly' | 'monthly' | 'quarterly'>;
}

export interface KpiTargetCatalog {
  categories: Record<string, { label: string; keys: Record<string, KpiTargetCatalogEntry> }>;
}

export const fetchBusinessParameterCatalog = async (): Promise<BusinessParameterCatalog> => {
  const res = await rmsApi.get('/api/v1/business_parameters/catalog');
  return res.data;
};

export const fetchKpiTargetCatalog = async (): Promise<KpiTargetCatalog> => {
  const res = await rmsApi.get('/api/v1/kpi_targets/catalog');
  return res.data;
};

export const fetchBusinessConfigLocations = async (): Promise<BusinessConfigLocation[]> => {
  const res = await rmsApi.get<{ locations: BusinessConfigLocation[] }>('/api/v1/business_parameters/locations');
  return res.data.locations || [];
};

export const fetchBusinessParameters = async (params: {
  location_id?: number | null;
  as_of?: string;
} = {}): Promise<{ parameters: BusinessParameter[]; company_id: number; location_id: number | null; as_of: string }> => {
  const res = await rmsApi.get('/api/v1/business_parameters', { params });
  return res.data;
};

export const fetchBusinessParameterHistory = async (params: {
  category: string;
  key: string;
  location_id?: number | null;
}): Promise<{ history: BusinessParameter[] }> => {
  const res = await rmsApi.get('/api/v1/business_parameters/history', { params });
  return res.data;
};

export const saveBusinessParameters = async (
  parameters: BusinessParameterSave[],
  opts: { location_id?: number | null; effective_from?: string } = {}
): Promise<{ created: BusinessParameter[] }> => {
  const res = await rmsApi.post('/api/v1/business_parameters', {
    parameters,
    location_id: opts.location_id,
    effective_from: opts.effective_from
  });
  return res.data;
};

export const fetchKpiTargets = async (params: {
  location_id?: number | null;
  as_of?: string;
} = {}): Promise<{ targets: KpiTarget[]; company_id: number; location_id: number | null; as_of: string }> => {
  const res = await rmsApi.get('/api/v1/kpi_targets', { params });
  return res.data;
};

export const fetchKpiTargetHistory = async (params: {
  category: string;
  metric_key: string;
  period_type: string;
  location_id?: number | null;
}): Promise<{ history: KpiTarget[] }> => {
  const res = await rmsApi.get('/api/v1/kpi_targets/history', { params });
  return res.data;
};

export const saveKpiTargets = async (
  targets: KpiTargetSave[],
  opts: { location_id?: number | null; effective_from?: string } = {}
): Promise<{ created: KpiTarget[] }> => {
  const res = await rmsApi.post('/api/v1/kpi_targets', {
    targets,
    location_id: opts.location_id,
    effective_from: opts.effective_from
  });
  return res.data;
};

